import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ChevronDown, ArrowRight } from 'lucide-react';
import { LogoMark } from '../components/LogoMark';
interface FaqItem {
  q: string;
  a: string;
}
interface FaqCategory {
  category: string;
  items: FaqItem[];
}
const FAQS: FaqCategory[] = [{
  category: 'General',
  items: [{
    q: 'What is Masube Settle?',
    a: 'Masube Settle is a USSD, SMS, and WhatsApp payment layer that lets your account holders settle their store accounts from any phone — no app, no data plan, and no queue at the till. You integrate once and we handle the customer-facing channels and settlement.'
  }, {
    q: 'Who is Masube built for?',
    a: 'We build for South African credit retailers — fashion, furniture, homeware, telco, and insurance — that carry large account books and serve many customers who are app-averse or on feature phones.'
  }, {
    q: 'How is this different from a normal payment gateway?',
    a: 'Standard gateways are built for card-not-present checkout. Masube is built for store-card collections: numbered USSD menus, balance lookups against your ledger, daily reconciliation files, and arrears workflows — the realities of a retail account book.'
  }]
}, {
  category: 'Integration',
  items: [{
    q: 'How long does it take to go live?',
    a: 'Most retailers are live within 14 days. You can integrate via a single REST API or use our pre-built connectors for SAP, Oracle, and Workday. Our solutions team supports you through the pilot.'
  }, {
    q: 'What do we need to build on our side?',
    a: "At minimum, push account balances to us (customer MSISDN, account reference, outstanding amount, due date) and consume a webhook or daily reconciliation file when payments settle. That's it — we handle the USSD, WhatsApp, and SMS experience."
  }, {
    q: 'Do our customers need to download anything?',
    a: 'No. Customers dial a USSD shortcode, send a WhatsApp message, or reply to an SMS. It works on every phone on every network, with or without data.'
  }]
}, {
  category: 'Payments & settlement',
  items: [{
    q: 'How fast do funds settle to us?',
    a: 'Payments settle via PayShap, typically within 60 seconds, and we drop a reconciliation file daily via SFTP or webhook. You see same-day (T+0) settlement to your nominated bank account.'
  }, {
    q: 'What happens if a payment fails?',
    a: 'Failed transactions are logged with a clear reason (timeout, invalid PIN, insufficient funds, network error, SIM-swap block) and surfaced in your dashboard. No money moves on a failed attempt, and customers can simply retry.'
  }, {
    q: 'How is pricing structured?',
    a: 'Pricing is per successful collection with no setup fee for founding members. Exact rates depend on your volume and channel mix — we cover this in your demo with a custom ROI projection.'
  }]
}, {
  category: 'Security & compliance',
  items: [{
    q: 'Do you ever ask customers for an OTP or PIN over USSD?',
    a: "Never. Customers authorise payments through their own bank via PayShap. We don't collect banking PINs or one-time passwords, which removes the most common phishing surface entirely."
  }, {
    q: 'How do you handle fraud and SIM swaps?',
    a: 'Every transaction is verified against mobile network operator records in real time. Recently ported or swapped numbers are automatically blocked from transacting, and all events are signed and timestamped for audit.'
  }, {
    q: 'Are you compliant with South African regulation?',
    a: 'Yes. We are PCI-DSS Level 1, POPIA compliant, FSCA aligned, and ISO 27001 certified. Audit-ready logs are available to your risk and compliance teams.'
  }]
}, {
  category: 'Waitlist & access',
  items: [{
    q: 'Why is the demo locked?',
    a: 'The interactive product demo unlocks once 100 retailers join the waitlist. Founding members get priority onboarding and pilot pricing — joining moves the whole list closer to unlocking.'
  }, {
    q: 'How do I move up the waitlist?',
    a: 'Refer other retailers using your share link. When a retailer you refer joins, you both move up the queue toward priority onboarding.'
  }]
}];
function FaqRow({
  item,
  isOpen,
  onToggle
}: {
  item: FaqItem;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-white/5" data-id="element-962">
      <button onClick={onToggle} aria-expanded={isOpen} className="w-full flex items-center justify-between gap-4 py-5 text-left group focus:outline-none focus-visible:ring-2 focus-visible:ring-mint rounded-lg" data-id="element-963">
        
        <span className="text-lg font-medium text-bone group-hover:text-mint transition-colors" data-id="element-964">
          {item.q}
        </span>
        <ChevronDown className={`w-5 h-5 text-muted flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180 text-mint' : ''}`} data-id="element-965" />
        
      </button>
      <AnimatePresence initial={false} data-id="element-966">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-967">
          
            <p className="text-bone-dim leading-relaxed pb-5 pr-8" data-id="element-968">{item.a}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function FAQ() {
  const [openKey, setOpenKey] = useState<string | null>('0-0');
  return <div className="min-h-screen flex flex-col w-full bg-ink font-sans text-bone selection:bg-mint selection:text-ink" data-id="element-969">
      {/* Minimal top bar */}
      <header className="border-b border-white/5" data-id="element-970">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between" data-id="element-971">
          <Link to="/" className="flex items-center gap-3" data-id="element-972">
            <LogoMark size={32} data-id="element-973" />
            <div className="flex items-baseline gap-1.5" data-id="element-974">
              <span className="font-semibold text-lg text-bone tracking-tight" data-id="element-975">
                Masube
              </span>
              <span className="text-xs font-medium text-mint uppercase tracking-[0.2em]" data-id="element-976">
                Settle
              </span>
            </div>
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-bone-dim hover:text-bone transition-colors" data-id="element-977">
            
            <ArrowLeft className="w-4 h-4" data-id="element-978" />
            Back to home
          </Link>
        </div>
      </header>

      <main className="flex-1" data-id="element-979">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-980">
          <div className="text-xs font-medium uppercase tracking-[0.2em] text-mint mb-4" data-id="element-981">
            Help center
          </div>
          <h1 className="display text-bone mb-4" style={{
          fontSize: 'clamp(2.25rem, 5vw, 3.5rem)'
        }} data-id="element-982">
            
            Frequently asked{' '}
            <span className="display-italic text-mint" data-id="element-983">questions</span>
          </h1>
          <p className="text-lg text-bone-dim mb-12 max-w-2xl" data-id="element-984">
            Everything retailers ask about integrating Masube Settle, settlement
            timing, security, and getting access.
          </p>

          <div className="space-y-12" data-id="element-985">
            {FAQS.map((cat, ci) => <section key={cat.category} data-id="element-986">
                <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-muted mb-2" data-id="element-987">
                  {cat.category}
                </h2>
                <div data-id="element-988">
                  {cat.items.map((item, ii) => {
                const key = `${ci}-${ii}`;
                return <FaqRow key={key} item={item} isOpen={openKey === key} onToggle={() => setOpenKey(openKey === key ? null : key)} data-id="element-989" />;
              })}
                </div>
              </section>)}
          </div>

          {/* CTA */}
          <div className="mt-16 bg-ink-2 border border-white/10 rounded-3xl p-8 text-center relative overflow-hidden" data-id="element-990">
            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-mint/10 blur-3xl pointer-events-none" data-id="element-991"></div>
            <div className="relative z-10" data-id="element-992">
              <h3 className="text-2xl font-semibold text-bone mb-3 tracking-tight" data-id="element-993">
                Still have questions?
              </h3>
              <p className="text-bone-dim mb-6 max-w-md mx-auto" data-id="element-994">
                Join the founding-retailer waitlist and our team will walk you
                through anything specific to your account book.
              </p>
              <a href="/#book-demo" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-mint text-ink font-semibold text-sm hover:bg-mint-dim transition-colors" data-id="element-995">
                
                Join the waitlist
                <ArrowRight className="w-4 h-4" data-id="element-996" />
              </a>
            </div>
          </div>
        </div>
      </main>

      <footer className="border-t border-white/5 py-8" data-id="element-997">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row justify-between items-center gap-3 text-sm text-muted" data-id="element-998">
          <span data-id="element-999">© {new Date().getFullYear()} Masube Settle (Pty) Ltd.</span>
          <div className="flex gap-6" data-id="element-1000">
            <Link to="/privacy" className="hover:text-bone transition-colors" data-id="element-1001">
              Privacy
            </Link>
            <Link to="/terms" className="hover:text-bone transition-colors" data-id="element-1002">
              Terms
            </Link>
            <Link to="/faq" className="hover:text-bone transition-colors" data-id="element-1003">
              FAQ
            </Link>
          </div>
        </div>
      </footer>
    </div>;
}